import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import { useSiteSettings, preloadPageTexts } from "@/hooks/useCms";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import MaintenancePage from "@/pages/MaintenancePage";

// --- Lazy-loaded páginas públicas de detalhe (só carregam quando são visitadas) ---
const SolucoesPage = lazy(() => import("@/pages/SolucoesPage"));
const ServicosPage = lazy(() => import("@/pages/ServicosPage"));
const ContactosPage = lazy(() => import("@/pages/ContactosPage"));
const ServicoRedirect = lazy(() => import("@/pages/ServicoRedirect"));
const SolucaoDetalhe = lazy(() => import("@/pages/SolucaoDetalhe"));
const NoticiasPage = lazy(() => import("@/pages/NoticiasPage"));
const NoticiaDetalhe = lazy(() => import("@/pages/NoticiaDetalhe"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const SobreLayout = lazy(() => import("@/pages/sobre/SobreLayout"));
const SobreGrupo = lazy(() => import("@/pages/sobre/SobreGrupo"));
const SobreCertificacoes = lazy(() => import("@/pages/sobre/SobreCertificacoes"));
const SobreOportunidades = lazy(() => import("@/pages/sobre/SobreOportunidades"));
const SobreNoticias = lazy(() => import("@/pages/sobre/SobreNoticias"));
const SobreBlog = lazy(() => import("@/pages/sobre/SobreBlog"));
const PoliticaPrivacidade = lazy(() => import("@/pages/PoliticaPrivacidade"));
const TermosCondicoes = lazy(() => import("@/pages/TermosCondicoes"));

// --- Widgets pesados: só carregam quando não é rota admin ---
const ChatWidget = lazy(() => import("@/components/ChatWidget"));
const GlobalDialogs = lazy(() => import("@/components/GlobalDialogs"));

// --- Lazy-loaded Admin CMS (~1MB, não carrega para visitantes públicos) ---
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AccountAdmin = lazy(() => import("@/pages/admin/AccountAdmin"));
const AcademyCoursesAdmin = lazy(() => import("@/pages/admin/AcademyCoursesAdmin"));
const PreInscricoesAdmin = lazy(() => import("@/pages/admin/PreInscricoesAdmin"));
const AcademiaPage = lazy(() => import("@/pages/AcademiaPage"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const ServicesAdmin = lazy(() => import("@/pages/admin/ServicesAdmin"));
const SolutionsAdmin = lazy(() => import("@/pages/admin/SolutionsAdmin"));
const NewsAdmin = lazy(() => import("@/pages/admin/NewsAdmin"));
const SubmissionsAdmin = lazy(() => import("@/pages/admin/SubmissionsAdmin"));
const ApplicationsAdmin = lazy(() => import("@/pages/admin/ApplicationsAdmin"));
const SmtpAdmin = lazy(() => import("@/pages/admin/SmtpAdmin"));
const AiSettingsAdmin = lazy(() => import("@/pages/admin/AiSettingsAdmin"));
const AiMetricsAdmin = lazy(() => import("@/pages/admin/AiMetricsAdmin"));
const LeadsAdmin = lazy(() => import("@/pages/admin/LeadsAdmin"));
const ConversationsAdmin = lazy(() => import("@/pages/admin/ConversationsAdmin"));
const AutoTagRulesAdmin = lazy(() => import("@/pages/admin/AutoTagRulesAdmin"));
const DiagnosticTemplateAdmin = lazy(() => import("@/pages/admin/DiagnosticTemplateAdmin"));
const BandejaAdmin = lazy(() => import("@/pages/admin/BandejaAdmin"));
const ServiceStatusAdmin = lazy(() => import("@/pages/admin/ServiceStatusAdmin"));
const GroupAdmin = lazy(() => import("@/pages/admin/GroupAdmin"));
const CertificationsAdmin = lazy(() => import("@/pages/admin/CertificationsAdmin"));
const OfficesAdmin = lazy(() => import("@/pages/admin/OfficesAdmin"));
const JobOpeningsAdmin = lazy(() => import("@/pages/admin/JobOpeningsAdmin"));
const PageTextsAdmin = lazy(() => import("@/pages/admin/PageTextsAdmin"));
const BrandingAdmin = lazy(() => import("@/pages/admin/BrandingAdmin"));
const FooterOrderAdmin = lazy(() => import("@/pages/admin/FooterOrderAdmin"));
const OrcamentoAdmin = lazy(() => import("@/pages/admin/OrcamentoAdmin"));
const AgendamentoAdmin = lazy(() => import("@/pages/admin/AgendamentoAdmin"));
const CoberturaAdmin = lazy(() => import("@/pages/admin/CoberturaAdmin"));

// Skeleton subtil que respeita o layout público (container + eyebrow + heading + grid).
// Aparece durante o download de um chunk lazy; usa animate-pulse do Tailwind para
// dar sensação de progresso sem "spinner" ruidoso.
function PageFallback() {
  return (
    <div
      className="min-h-[70vh] py-14 sm:py-20"
      data-testid="page-loading-fallback"
      aria-busy="true"
      aria-live="polite"
      role="status"
    >
      <span className="sr-only">A carregar…</span>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 animate-pulse">
        {/* eyebrow */}
        <div className="h-3 w-24 rounded-full bg-slate-200/80 dark:bg-white/10" />
        {/* heading */}
        <div className="mt-5 h-9 sm:h-11 w-3/4 max-w-xl rounded-md bg-slate-200/80 dark:bg-white/10" />
        <div className="mt-3 h-9 sm:h-11 w-1/2 max-w-md rounded-md bg-slate-200/80 dark:bg-white/10" />
        {/* subtítulo */}
        <div className="mt-6 h-4 w-full max-w-xl rounded bg-slate-100 dark:bg-white/5" />
        <div className="mt-2 h-4 w-4/5 max-w-lg rounded bg-slate-100 dark:bg-white/5" />
        {/* grid de cards */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-52 rounded-xl border border-slate-200/70 dark:border-white/10 bg-slate-100/70 dark:bg-white/5"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AppShell({ children }) {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin");
  const settings = useSiteSettings();
  const inMaintenance = !!settings?.maintenance_mode && !isAdmin;

  // Pré-aquece o cache dos textos das páginas mais visitadas para evitar
  // flash de fallback ao navegar entre abas (ex: /sobre/*).
  useEffect(() => {
    if (isAdmin) return;
    preloadPageTexts([
      "sobre-hero",
      "sobre-grupo",
      "sobre-certificacoes",
      "sobre-oportunidades",
      "academia-hero",
      "academia-intro",
      "academia-formacoes",
      "academia-form",
      "sobre-blog",
      "home-porque",
      "home-numeros",
      "footer-tagline",
    ]);
  }, [isAdmin]);

  if (inMaintenance) {
    return <MaintenancePage />;
  }

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Navbar />}
      <main className={isAdmin ? "" : "min-h-[70vh]"}>{children}</main>
      {!isAdmin && <Footer />}
      {!isAdmin && (
        <Suspense fallback={null}>
          <ChatWidget />
          <GlobalDialogs />
        </Suspense>
      )}
      {!isAdmin && <BackToTop />}
    </>
  );
}

function App() {
  useEffect(() => {
    // Disable Lenis smooth scroll in admin area (admin routes don't need smooth scrolling)
    const isAdminRoute = window.location.pathname.startsWith("/admin");
    if (isAdminRoute) {
      return; // Skip Lenis initialization for admin routes
    }

    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);

    // Pause Lenis when a Radix Dialog (or any component) locks body scroll.
    // Radix UI sets `data-scroll-locked` on <body> when a modal is open.
    const syncLock = () => {
      const locked =
        document.body.hasAttribute("data-scroll-locked") ||
        document.body.style.overflow === "hidden" ||
        document.documentElement.style.overflow === "hidden";
      if (locked) lenis.stop();
      else lenis.start();
    };
    syncLock();
    const observer = new MutationObserver(syncLock);
    observer.observe(document.body, { attributes: true, attributeFilter: ["data-scroll-locked", "style"] });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });

    return () => {
      observer.disconnect();
      lenis.destroy();
    };
  }, []);

  return (
    <div className="bg-white text-[#0a0a0a] dark:bg-[#0A0E16] dark:text-slate-100 transition-colors duration-300">
      <BrowserRouter>
        <AuthProvider>
          <AppShell>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Público */}
                <Route path="/" element={<Home />} />
                <Route path="/solucoes" element={<SolucoesPage />} />
                <Route path="/solucoes/:slug" element={<SolucaoDetalhe />} />
                <Route path="/servicos" element={<ServicosPage />} />
                <Route path="/servicos/:slug" element={<ServicoRedirect />} />
                <Route path="/sobre" element={<SobreLayout />}>
                  <Route index element={<Navigate to="/sobre/grupo" replace />} />
                  <Route path="grupo" element={<SobreGrupo />} />
                  <Route path="certificacoes" element={<SobreCertificacoes />} />
                  <Route path="oportunidades" element={<SobreOportunidades />} />
                  <Route path="noticias" element={<SobreNoticias />} />
                  <Route path="blog" element={<SobreBlog />} />
                </Route>
                <Route path="/academia" element={<AcademiaPage />} />
                <Route path="/contactos" element={<ContactosPage />} />
                <Route path="/noticias" element={<NoticiasPage />} />
                <Route path="/noticias/:slug" element={<NoticiaDetalhe />} />
                <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
                <Route path="/termos-condicoes" element={<TermosCondicoes />} />

                {/* Admin CMS */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/admin/bandeja" replace />} />
                  <Route path="bandeja" element={<BandejaAdmin />} />
                  <Route path="leads" element={<LeadsAdmin />} />
                  <Route path="servicos" element={<ServicesAdmin />} />
                  <Route path="solucoes" element={<SolutionsAdmin />} />
                  <Route path="noticias" element={<NewsAdmin />} />
                  <Route path="pedidos" element={<SubmissionsAdmin />} />
                  <Route path="candidaturas" element={<ApplicationsAdmin />} />
                  <Route path="conversas" element={<ConversationsAdmin />} />
                  <Route path="conversas/:sessionId" element={<ConversationsAdmin />} />
                  <Route path="smtp" element={<SmtpAdmin />} />
                  <Route path="site-settings" element={<Navigate to="/admin/noticias" replace />} />
                  <Route path="ia" element={<AiSettingsAdmin />} />
                  <Route path="ia/metricas" element={<AiMetricsAdmin />} />
                  <Route path="ia/diagnostico" element={<DiagnosticTemplateAdmin />} />
                  <Route path="ia/orcamento" element={<OrcamentoAdmin />} />
                  <Route path="ia/agendamento" element={<AgendamentoAdmin />} />
                  <Route path="auto-tag-rules" element={<AutoTagRulesAdmin />} />
                  <Route path="servicos-status" element={<ServiceStatusAdmin />} />
                  <Route path="grupo" element={<GroupAdmin />} />
                  <Route path="certificacoes" element={<CertificationsAdmin />} />
                  <Route path="escritorios" element={<OfficesAdmin />} />
                  <Route path="oportunidades" element={<JobOpeningsAdmin />} />
                  <Route path="textos" element={<PageTextsAdmin />} />
                  <Route path="branding" element={<BrandingAdmin />} />
                  <Route path="ordem-rodape" element={<FooterOrderAdmin />} />
                  <Route path="cobertura" element={<CoberturaAdmin />} />
                  <Route path="conta" element={<AccountAdmin />} />
                  <Route path="academia" element={<AcademyCoursesAdmin />} />
                  <Route path="pre-inscricoes" element={<PreInscricoesAdmin />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AppShell>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
